import {DateTime} from 'luxon';
import {cloneDeep} from 'lodash-es';
import type {
    OpeningCalendarDay,
    OpeningCalendarData,
    OpeningCalendarHoursRange,
    ViewableOpeningCalendarData
} from './types';


const TIME_FORMAT = 'H:mm';

export function normalizeRanges(originalData: OpeningCalendarData, showWeekend: boolean | 'auto'): ViewableOpeningCalendarData {

    const data: ViewableOpeningCalendarData = {...cloneDeep(originalData), count: undefined};

    let shouldShowWeekend = false;
    if (showWeekend === 'auto') {
        shouldShowWeekend = data.days[5].ranges.length + data.days[6].ranges.length > 0;
    } else {
        shouldShowWeekend = showWeekend;
    }

    if (!shouldShowWeekend) {
        data.days = data.days.slice(0, 5);
    }

    data.count = data.days
        .map((day) => day.ranges.length)
        .reduce((acc, ranges) => Math.max(acc, ranges));

    data.days = data.days.map((day) => {
        for (let i = day.ranges.length; i < data.count; i++) {
            day.ranges.push(null);
        }
        return day;
    });

    const pivots = countPivots(data.days, data.count);

    data.days = sortRanges(data.days, pivots, data.count);

    return data;
}

export function countPivots(days: OpeningCalendarDay[], count: number): DateTime[] {
    const fullRanges = days
        // transformuje dny na pole ranges => [[{}, {}], [{}, {}]]
        .map((day) => day.ranges)
        // vyfiltruje plne ranges
        .filter((ranges) => ranges.every((range) => range !== null));

    const countOfFullRanges = fullRanges.length;

    return fullRanges
        // vypocita stredni hodnotu kazde range
        .map((ranges) => ranges.map(countRangeAvg))
        // secte vsechny stredni hodnoty ve sloupecku
        .reduce<number[]>((accumulator, ranges) =>
            ranges.map((oneRange, index) => oneRange + accumulator[index]), new Array(count).fill(0))
        // ze sumy strednich hodnot vypocita finalni stredni hodnotu a vrati DateTime
        .map((sum) => DateTime.fromMillis(sum / countOfFullRanges));
}

export function sortRanges(days: OpeningCalendarDay[], pivots: DateTime[], count: number): OpeningCalendarDay[] {
    days.forEach((day) => {
        // pokud se jedna o den s ne vsema rangema
        if (day.ranges.some((range) => range === null)) {
            const indexes = day.ranges
                // vypocita stredni hodnotu ke kazde range
                .map(countRangeAvg)
                .map((range) => {
                    // tady se vypocitaji odchylky stredni hodnoty range od kazdeho pivotu
                    const diffs = range === null ? null : pivots.map((pivot) => Math.abs(pivot.valueOf() - range));

                    if (diffs === null) {
                        return null
                    }
                    // tady se vrati index pivotu s minimalni odchylkou
                    return diffs.reduce((acc, e, i) => acc.min > e ? {i, min: e} : acc,
                        {i: 0, min: Number.MAX_SAFE_INTEGER}
                    ).i;
                });

            const newRanges = new Array(count).fill(null);
            for (let i = 0; i < day.ranges.length; i++) {
                if (newRanges[i] === null) {
                    newRanges[indexes[i]] = day.ranges[i];
                }
            }

            day.ranges = newRanges;
        }
    });

    return days;
}

export function countRangeAvg(range: OpeningCalendarHoursRange): number | null {
    if (range === null) {
        return null;
    }

    const dFrom = DateTime.fromFormat(range.fromTime, TIME_FORMAT);
    const dTo = DateTime.fromFormat(range.toTime, TIME_FORMAT);
    return (dFrom.valueOf() + dTo.valueOf()) / 2;
}